import {
  type AddGitLinkOverrideInput,
  type CreateGitBranchInput,
  type CreateGitChangeRequestBody,
  type CreateGitConnectionInput,
  type CreateGitHostInput,
  type GitBranchCreated,
  type GitBranchList,
  type GitChangeRequestCreated,
  type GitChangeRequestDetail,
  type GitChangeRequestPage,
  type GitChangeRequestState,
  type GitCommitPage,
  type GitCrSummaryState,
  type GitConnectionView,
  type GitDeploymentPage,
  type GitHostOption,
  type GitHostTestResult,
  type GitHostView,
  type GitLinkOverrideView,
  type GitLinkTargetKind,
  type GitProjectSettings,
  type GitPipelineList,
  type GitProjectSettingsView,
  type GitReleaseDetail,
  type GitReleasePage,
  type GitRemoteGroupPage,
  type GitRemoteRepositoryPage,
  type ApplyGitRepositorySelectionInput,
  type GitRepositoryGroupLinkResult,
  type GitRepositoryGroupView,
  type GitRepositorySelection,
  type GitRepositorySelectionResult,
  type LinkGitRepositoryGroupInput,
  type GitRepositorySettingsPatch,
  type GitRepositoryView,
  type GitRulesPreview,
  type GitSyncRequestResult,
  type GitTargetLinks,
  type GitTaskMarks,
  type GitWebhookChangeResult,
  type GitWebhookMode,
  type GitWebhookSettingsView,
  type LinkGitRepositoryInput,
  type RunGitCrSummaryInput,
  type GitWorkItemRef,
  type GitWriteOptions,
  type UpdateGitHostInput,
  type WriteGitCrSummaryInput,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

/**
 * Máy chủ git của tổ chức — APM-GIT-b, docs/plan-git-integration.md §3.1;
 * thuộc tổ chức từ APM-ORGINT-c/-e. Cửa `integration:manage` (admin tổ chức).
 *
 * ⚠️ Truyền **object** cho `body`, không `JSON.stringify` — `apiFetch` tự
 * stringify (bẫy APM-805 trong CLAUDE.md).
 */
export const gitHostAdminApi = {
  list: () => apiFetch<GitHostView[]>('/admin/git-hosts'),

  create: (body: CreateGitHostInput) =>
    apiFetch<GitHostView>('/admin/git-hosts', { method: 'POST', body }),

  update: (id: string, body: UpdateGitHostInput) =>
    apiFetch<GitHostView>(`/admin/git-hosts/${id}`, { method: 'PATCH', body }),

  remove: (id: string) =>
    apiFetch<{ deleted: true }>(`/admin/git-hosts/${id}`, { method: 'DELETE' }),

  /** Nút Kiểm tra: máy chủ được gọi **không token** — một câu hỏi, không đổi gì. */
  test: (id: string) =>
    apiFetch<GitHostTestResult>(`/admin/git-hosts/${id}/test`, { method: 'POST' }),
};

export const gitHostKeys = {
  admin: ['admin', 'git-hosts'] as const,
};

// ── Tab Git của một dự án — APM-GIT-e (endpoint của APM-GIT-d) ───────────────

const base = (projectId: string): string => `/projects/${projectId}/git`;

/** Chuỗi query từ các tham số có giá trị — `undefined` và chuỗi rỗng bị bỏ. */
function withQuery(path: string, params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `${path}?${text}` : path;
}

/** Bộ lọc của bảng MR (`GitChangeRequestListQuery` trừ con trỏ và mốc). */
export interface GitChangeRequestFilters {
  readonly repositoryId?: string | undefined;
  readonly state: 'all' | GitChangeRequestState;
  readonly linked: 'all' | 'linked' | 'unlinked';
}

/** Một việc mà khối "Phát triển" đang nói tới. */
export interface GitTargetRef {
  readonly kind: GitLinkTargetKind;
  readonly id: string;
}

/**
 * Mọi lời gọi của tab Git và khối "Phát triển" — docs/plan-git-integration.md
 * §8.1, §8.2. Ba cửa quyền (§8.7): `git:read` đọc, `git:act` gắn / ẩn liên kết
 * và "Kéo ngay", `git:manage` kết nối, kho, cấu hình.
 *
 * Đường đọc bản sao nhận `asOf` (ISO **UTC `Z`** — `normalizeGitAsOf` ở
 * `git-format.ts`); cấu hình (kết nối, kho, cài đặt) thì không (§7.3).
 *
 * ⚠️ Token chỉ đi trong `body`. Truyền **object**, không `JSON.stringify`.
 */
export const gitApi = {
  // Cài đặt — `git:manage`.
  hosts: (projectId: string) => apiFetch<GitHostOption[]>(`${base(projectId)}/hosts`),

  connections: (projectId: string) =>
    apiFetch<GitConnectionView[]>(`${base(projectId)}/connections`),

  createConnection: (projectId: string, body: CreateGitConnectionInput) =>
    apiFetch<GitConnectionView>(`${base(projectId)}/connections`, { method: 'POST', body }),

  rotateToken: (projectId: string, connectionId: string, token: string) =>
    apiFetch<GitConnectionView>(`${base(projectId)}/connections/${connectionId}/token`, {
      method: 'PUT',
      body: { token },
    }),

  revokeConnection: (projectId: string, connectionId: string) =>
    apiFetch<{ revoked: true }>(`${base(projectId)}/connections/${connectionId}`, {
      method: 'DELETE',
    }),

  /** Kho mà token của kết nối thấy — gọi thẳng máy chủ git, nên chỉ gọi khi người dùng bấm. */
  remoteRepositories: (projectId: string, connectionId: string, search?: string, cursor?: string) =>
    apiFetch<GitRemoteRepositoryPage>(
      withQuery(`${base(projectId)}/connections/${connectionId}/remote-repositories`, {
        search,
        cursor,
      }),
    ),

  // Gắn cả nhóm kho — APM-GIT-s (§16.6). Mọi route `git:manage`; ba route đầu và
  // danh sách quản lý gọi máy chủ git **live**, nên chỉ gọi khi người dùng mở / bấm.

  /** Nhóm GitLab / organization GitHub mà token của kết nối thấy. */
  remoteGroups: (projectId: string, connectionId: string, search?: string, cursor?: string) =>
    apiFetch<GitRemoteGroupPage>(
      withQuery(`${base(projectId)}/connections/${connectionId}/remote-groups`, {
        search,
        cursor,
      }),
    ),

  groups: (projectId: string) => apiFetch<GitRepositoryGroupView[]>(`${base(projectId)}/groups`),

  linkGroup: (projectId: string, body: LinkGitRepositoryGroupInput) =>
    apiFetch<GitRepositoryGroupLinkResult>(`${base(projectId)}/groups`, { method: 'POST', body }),

  /** Gỡ nhóm — kho đã gắn qua nhóm giữ nguyên (gỡ luôn thì gọi tiếp `applySelection`). */
  unlinkGroup: (projectId: string, groupId: string) =>
    apiFetch<{ unlinked: true }>(`${base(projectId)}/groups/${groupId}`, { method: 'DELETE' }),

  /** Danh sách quản lý kho: kho của các nhóm đã gắn (kèm trạng thái) + kho gắn tay. */
  repositorySelection: (projectId: string) =>
    apiFetch<GitRepositorySelection>(`${base(projectId)}/repositories/selection`),

  /** Áp lựa chọn — **chỉ** mục được nêu mới đổi. */
  applySelection: (projectId: string, body: ApplyGitRepositorySelectionInput) =>
    apiFetch<GitRepositorySelectionResult>(`${base(projectId)}/repositories/selection`, {
      method: 'PUT',
      body,
    }),

  // Kho — đọc là `git:read`.
  repositories: (projectId: string) =>
    apiFetch<GitRepositoryView[]>(`${base(projectId)}/repositories`),

  linkRepository: (projectId: string, body: LinkGitRepositoryInput) =>
    apiFetch<GitRepositoryView>(`${base(projectId)}/repositories`, { method: 'POST', body }),

  updateRepository: (projectId: string, repositoryId: string, body: GitRepositorySettingsPatch) =>
    apiFetch<GitRepositoryView>(`${base(projectId)}/repositories/${repositoryId}`, {
      method: 'PATCH',
      body,
    }),

  unlinkRepository: (projectId: string, repositoryId: string) =>
    apiFetch<{ unlinked: true }>(`${base(projectId)}/repositories/${repositoryId}`, {
      method: 'DELETE',
    }),

  /** "Kéo ngay" (`git:act`). `queued: false` = đã có lượt đang chờ / đang chạy. */
  requestSync: (projectId: string, repositoryId: string) =>
    apiFetch<GitSyncRequestResult>(`${base(projectId)}/repositories/${repositoryId}/sync`, {
      method: 'POST',
    }),

  // Đọc bản sao — `git:read`, nhận `asOf`.
  changeRequests: (
    projectId: string,
    filters: GitChangeRequestFilters,
    asOf?: string,
    cursor?: string,
  ) =>
    apiFetch<GitChangeRequestPage>(
      withQuery(`${base(projectId)}/change-requests`, {
        repositoryId: filters.repositoryId,
        state: filters.state,
        linked: filters.linked,
        cursor,
        asOf,
      }),
    ),

  changeRequest: (projectId: string, repositoryId: string, number: number, asOf?: string) =>
    apiFetch<GitChangeRequestDetail>(
      withQuery(`${base(projectId)}/repositories/${repositoryId}/change-requests/${number}`, {
        asOf,
      }),
    ),

  branches: (projectId: string, repositoryId?: string, asOf?: string) =>
    apiFetch<GitBranchList>(withQuery(`${base(projectId)}/branches`, { repositoryId, asOf })),

  commits: (projectId: string, repositoryId?: string, asOf?: string, cursor?: string) =>
    apiFetch<GitCommitPage>(
      withQuery(`${base(projectId)}/commits`, { repositoryId, cursor, asOf }),
    ),

  // Liên kết của một việc — khối "Phát triển".
  links: (projectId: string, target: GitTargetRef, asOf?: string) =>
    apiFetch<GitTargetLinks>(
      withQuery(`${base(projectId)}/links`, {
        targetKind: target.kind,
        targetId: target.id,
        asOf,
      }),
    ),

  addLinkOverride: (projectId: string, body: AddGitLinkOverrideInput) =>
    apiFetch<GitLinkOverrideView>(`${base(projectId)}/link-overrides`, { method: 'POST', body }),

  removeLinkOverride: (projectId: string, overrideId: string) =>
    apiFetch<{ removed: true }>(`${base(projectId)}/link-overrides/${overrideId}`, {
      method: 'DELETE',
    }),

  // CI, bản phát hành, deployment — APM-GIT-i, `git:read`, nhận `asOf`.
  releases: (projectId: string, repositoryId?: string, asOf?: string, cursor?: string) =>
    apiFetch<GitReleasePage>(
      withQuery(`${base(projectId)}/releases`, { repositoryId, cursor, asOf }),
    ),

  /** Cùng đường dẫn với `releaseNotesApi.release` — và cùng khoá cache (`gitKeys.release`). */
  release: (projectId: string, releaseId: string, asOf?: string) =>
    apiFetch<GitReleaseDetail>(withQuery(`${base(projectId)}/releases/${releaseId}`, { asOf })),

  deployments: (projectId: string, repositoryId?: string, asOf?: string, cursor?: string) =>
    apiFetch<GitDeploymentPage>(
      withQuery(`${base(projectId)}/deployments`, { repositoryId, cursor, asOf }),
    ),

  /** Lượt CI gần nhất; vắng `ref` = nhánh mặc định của từng kho. */
  pipelines: (projectId: string, repositoryId?: string, limit?: number, asOf?: string) =>
    apiFetch<GitPipelineList>(
      withQuery(`${base(projectId)}/pipelines`, { repositoryId, limit, asOf }),
    ),

  /** Dấu MR đang mở cho **cả bảng Kanban**, một lời gọi (§8.2). */
  taskMarks: (projectId: string, asOf?: string) =>
    apiFetch<GitTaskMarks>(withQuery(`${base(projectId)}/task-marks`, { asOf })),

  // Cấu hình của dự án (`git_project_settings`) — đọc `git:read`, ghi `git:manage`.
  settings: (projectId: string) => apiFetch<GitProjectSettingsView>(`${base(projectId)}/settings`),

  /** Gửi **trọn bộ**: phần mình không sửa (luật, bảng `{type}`) phải gửi lại nguyên. */
  saveSettings: (projectId: string, body: GitProjectSettings) =>
    apiFetch<GitProjectSettingsView>(`${base(projectId)}/settings`, { method: 'PUT', body }),

  /**
   * Màn cấu hình luật tự đổi trạng thái — APM-GIT-h, `git:manage`. Mang sẵn ngữ
   * cảnh để web gọi lại `validateGitRules` trên bản đang sửa, và 20 dòng sổ gần nhất.
   */
  rulesPreview: (projectId: string) =>
    apiFetch<GitRulesPreview>(`${base(projectId)}/rules/preview`),

  // Webhook "kéo ngay" của một kho — APM-GIT-g, `git:manage`.
  webhook: (projectId: string, repositoryId: string) =>
    apiFetch<GitWebhookSettingsView>(`${base(projectId)}/repositories/${repositoryId}/webhook`),

  /** `manual` trả `secret` **một lần** ở đúng phản hồi này; gọi lại là sinh bí mật mới. */
  changeWebhook: (projectId: string, repositoryId: string, mode: GitWebhookMode) =>
    apiFetch<GitWebhookChangeResult>(`${base(projectId)}/repositories/${repositoryId}/webhook`, {
      method: 'PUT',
      body: { mode },
    }),

  // Ghi ngược (`git:act`) — APM-GIT-k, §8.4. Chạy dưới tên tài khoản của token.
  writeOptions: (projectId: string, target: GitTargetRef) =>
    apiFetch<GitWriteOptions>(
      withQuery(`${base(projectId)}/write-options`, {
        targetKind: target.kind,
        targetId: target.id,
      }),
    ),

  createBranch: (projectId: string, repositoryId: string, body: CreateGitBranchInput) =>
    apiFetch<GitBranchCreated>(`${base(projectId)}/repositories/${repositoryId}/branches`, {
      method: 'POST',
      body,
    }),

  createChangeRequest: (
    projectId: string,
    repositoryId: string,
    body: CreateGitChangeRequestBody,
  ) =>
    apiFetch<GitChangeRequestCreated>(
      `${base(projectId)}/repositories/${repositoryId}/change-requests`,
      { method: 'POST', body },
    ),

  /** Đường dẫn sâu `/t/GOLD-123` (`git:read`) — mã việc → việc. */
  workItem: (code: string) =>
    apiFetch<GitWorkItemRef>(`/git/work-items/${encodeURIComponent(code)}`),
};

/**
 * Khoá cache. Mọi khoá bắt đầu bằng `['git', projectId]` — một lần
 * `invalidateQueries({ queryKey: gitKeys.project(projectId) })` làm mới cả tab
 * lẫn khối "Phát triển" sau một thao tác ghi.
 */
export const gitKeys = {
  project: (projectId: string) => ['git', projectId] as const,
  hosts: (projectId: string) => ['git', projectId, 'hosts'] as const,
  connections: (projectId: string) => ['git', projectId, 'connections'] as const,
  remoteRepositories: (projectId: string, connectionId: string, search: string) =>
    ['git', projectId, 'remote-repositories', connectionId, search] as const,
  repositories: (projectId: string) => ['git', projectId, 'repositories'] as const,
  remoteGroups: (projectId: string, connectionId: string, search: string) =>
    ['git', projectId, 'remote-groups', connectionId, search] as const,
  repositorySelection: (projectId: string) => ['git', projectId, 'repository-selection'] as const,
  changeRequests: (projectId: string, filters: GitChangeRequestFilters, asOf?: string) =>
    [
      'git',
      projectId,
      'change-requests',
      filters.repositoryId ?? 'all',
      filters.state,
      filters.linked,
      asOf ?? 'now',
    ] as const,
  changeRequest: (projectId: string, repositoryId: string, number: number, asOf?: string) =>
    ['git', projectId, 'change-request', repositoryId, number, asOf ?? 'now'] as const,
  branches: (projectId: string, repositoryId?: string, asOf?: string) =>
    ['git', projectId, 'branches', repositoryId ?? 'all', asOf ?? 'now'] as const,
  commits: (projectId: string, repositoryId?: string, asOf?: string) =>
    ['git', projectId, 'commits', repositoryId ?? 'all', asOf ?? 'now'] as const,
  links: (projectId: string, target: GitTargetRef, asOf?: string) =>
    ['git', projectId, 'links', target.kind, target.id, asOf ?? 'now'] as const,
  releases: (projectId: string, repositoryId?: string, asOf?: string) =>
    ['git', projectId, 'releases', repositoryId ?? 'all', asOf ?? 'now'] as const,
  /** ⚠️ Trùng hình với `releaseNotesKeys.release` có chủ ý: hai nơi dùng chung một lần tải. */
  release: (projectId: string, releaseId: string, asOf?: string) =>
    ['git', projectId, 'release', releaseId, asOf ?? 'now'] as const,
  deployments: (projectId: string, repositoryId?: string, asOf?: string) =>
    ['git', projectId, 'deployments', repositoryId ?? 'all', asOf ?? 'now'] as const,
  pipelines: (projectId: string, repositoryId?: string, limit?: number, asOf?: string) =>
    [
      'git',
      projectId,
      'pipelines',
      repositoryId ?? 'all',
      limit ?? 'default',
      asOf ?? 'now',
    ] as const,
  taskMarks: (projectId: string, asOf?: string) =>
    ['git', projectId, 'task-marks', asOf ?? 'now'] as const,
  settings: (projectId: string) => ['git', projectId, 'settings'] as const,
  webhook: (projectId: string, repositoryId: string) =>
    ['git', projectId, 'webhook', repositoryId] as const,
  rulesPreview: (projectId: string) => ['git', projectId, 'rules-preview'] as const,
  writeOptions: (projectId: string, target: GitTargetRef) =>
    ['git', projectId, 'write-options', target.kind, target.id] as const,
};

// ── Tóm tắt MR bằng AI — APM-GIT-m (§8.5) ────────────────────────────────────

const summaryPath = (projectId: string, repositoryId: string, number: number): string =>
  `${base(projectId)}/repositories/${repositoryId}/change-requests/${number}/summary`;

/**
 * Lời tóm tắt của một MR (`git_cr_summary`). Đọc `git:read` (nhận `asOf`); chạy
 * skill `git:read` (tiền AI vào key người bấm); duyệt / sửa tay `git:act`. Phản hồi
 * chỉ mang **lời tóm tắt**, không bao giờ mang diff.
 */
export const gitCrSummaryApi = {
  state: (projectId: string, repositoryId: string, number: number, asOf?: string) =>
    apiFetch<GitCrSummaryState>(withQuery(summaryPath(projectId, repositoryId, number), { asOf })),

  run: (projectId: string, repositoryId: string, number: number, body: RunGitCrSummaryInput) =>
    apiFetch<GitCrSummaryState>(summaryPath(projectId, repositoryId, number), {
      method: 'POST',
      body,
    }),

  approve: (projectId: string, repositoryId: string, number: number) =>
    apiFetch<GitCrSummaryState>(`${summaryPath(projectId, repositoryId, number)}/approve`, {
      method: 'POST',
    }),

  write: (projectId: string, repositoryId: string, number: number, body: WriteGitCrSummaryInput) =>
    apiFetch<GitCrSummaryState>(summaryPath(projectId, repositoryId, number), {
      method: 'PUT',
      body,
    }),
};

export const gitCrSummaryKeys = {
  state: (projectId: string, repositoryId: string, number: number, asOf?: string) =>
    ['git', projectId, 'cr-summary', repositoryId, number, asOf ?? 'now'] as const,
};
